import { PROJECTS } from '@brickup/shared/locales/de/projects'
import type { Project } from '@brickup/shared/models'

/**
 * One order, field and direction together.
 *
 * Split into a field and a direction toggle they read as two decisions and
 * the toggle had to be named without knowing the field — which is how a price
 * came to be sorted "A–Z". A finished sentence per order says what happens.
 */
export type ProjectSort =
  | 'newest'
  | 'priceAsc'
  | 'priceDesc'
  | 'areaDesc'
  | 'areaAsc'
  | 'roomsDesc'

export const DEFAULT_PROJECT_SORT: ProjectSort = 'newest'

const area = (project: Project) => project.property?.livingArea ?? 0
const price = (project: Project) => project.property?.marketPrice ?? 0
const rooms = (project: Project) => project.property?.numberOfRooms ?? 0

/**
 * Every order a list of projects offers, in the order they are offered.
 *
 * One table, because the list and the horizontal row both sort the same
 * projects — each held its own copy, and only one of them treated a missing
 * value as zero.
 */
export const PROJECT_SORTS: {
  id: ProjectSort
  label: string
  compare: (a: Project, b: Project) => number
}[] = [
  // Newest is the id: a project's row is written once and never renumbered.
  {
    id: 'newest',
    label: PROJECTS.sort.newest,
    compare: (a, b) => b.id - a.id,
  },
  {
    id: 'priceAsc',
    label: PROJECTS.sort.priceAsc,
    compare: (a, b) => price(a) - price(b),
  },
  {
    id: 'priceDesc',
    label: PROJECTS.sort.priceDesc,
    compare: (a, b) => price(b) - price(a),
  },
  {
    id: 'areaDesc',
    label: PROJECTS.sort.areaDesc,
    compare: (a, b) => area(b) - area(a),
  },
  {
    id: 'areaAsc',
    label: PROJECTS.sort.areaAsc,
    compare: (a, b) => area(a) - area(b),
  },
  {
    id: 'roomsDesc',
    label: PROJECTS.sort.roomsDesc,
    compare: (a, b) => rooms(b) - rooms(a),
  },
]

export function projectSortLabel(sort: ProjectSort): string {
  return (PROJECT_SORTS.find(entry => entry.id === sort) ?? PROJECT_SORTS[0])
    .label
}

export function sortProjects(
  projects: Project[],
  sort: ProjectSort,
): Project[] {
  const entry = PROJECT_SORTS.find(candidate => candidate.id === sort)
  if (!entry) return projects

  return [...projects].sort(entry.compare)
}
